/**
 * Turning parsed text into loggable food.
 *
 * Each parsed fragment is matched against the food database and converted into
 * macros. Anything that cannot be resolved confidently is returned as
 * unresolved rather than guessed: a wrong gram weight silently becomes a wrong
 * calorie count, and a food log that quietly lies is worse than one that asks.
 */
import { forGrams } from './macros';
import { bestMatch, gramsFor, searchFoods, type CoreFood } from './foodsearch';
import type { ParsedItem } from './foodparse';
import type { LoggedFood, Macros } from '../data/schema';

export interface ResolvedItem {
  /** The parsed input this came from. */
  parsed: ParsedItem;
  /** The matched food, when one was found. */
  food: CoreFood | null;
  /** Alternatives, so the user can correct a wrong match in one tap. */
  alternatives: CoreFood[];
  /** Grams resolved for the stated amount, when resolvable. */
  grams: number | null;
  /** Macros for that amount. Null when the amount could not be resolved. */
  macros: Macros | null;
  /** Why this needs attention, if it does. */
  issue: 'no-match' | 'no-amount' | null;
}

/** Per-100 g macros from a database row. */
export function macrosOf(food: CoreFood): Macros {
  const m: Macros = { kcal: food.k, protein: food.p, carbs: food.c, fat: food.f };
  if (food.fi !== undefined) m.fiber = food.fi;
  if (food.s !== undefined) m.sugar = food.s;
  return m;
}

/** Resolve one parsed fragment. */
export function resolveItem(item: ParsedItem, foods: CoreFood[]): ResolvedItem {
  const matches = searchFoods(foods, item.query, 6);
  const food = matches[0]?.food ?? null;
  if (!food) {
    return { parsed: item, food: null, alternatives: [], grams: null, macros: null, issue: 'no-match' };
  }
  const grams = gramsFor(food, item.qty, item.unit);
  const alternatives = matches.slice(1).map((m) => m.food);
  if (grams === null) {
    return { parsed: item, food, alternatives, grams: null, macros: null, issue: 'no-amount' };
  }
  return {
    parsed: item,
    food,
    alternatives,
    grams,
    macros: forGrams(macrosOf(food), grams),
    issue: null,
  };
}

export function resolveAll(items: ParsedItem[], foods: CoreFood[]): ResolvedItem[] {
  return items.map((item) => resolveItem(item, foods));
}

/** Re-resolve after the user picks a different food or changes the amount. */
export function reresolve(item: ResolvedItem, food: CoreFood, qty: number, unit: string | null): ResolvedItem {
  const grams = gramsFor(food, qty, unit);
  const parsed = { ...item.parsed, qty, unit, assumedQty: false };
  if (grams === null) {
    return { ...item, parsed, food, grams: null, macros: null, issue: 'no-amount' };
  }
  return { ...item, parsed, food, grams, macros: forGrams(macrosOf(food), grams), issue: null };
}

/** Convert a resolved item into the shape that gets stored. */
export function toLoggedFood(item: ResolvedItem): LoggedFood | null {
  if (!item.macros || !item.food || item.grams === null) return null;
  return {
    foodId: item.food.i,
    name: item.food.n,
    qty: item.parsed.qty,
    unit: item.parsed.unit ?? (item.food.po?.[0]?.[0] ?? 'serving'),
    macros: item.macros,
    ...(item.parsed.assumedQty ? { estimated: true } : {}),
  };
}

/**
 * Resolve items the language model structured. The model's query is used to
 * find the database row; its gram estimate is used only when the database
 * cannot resolve the stated amount itself.
 */
export function resolveLlmItems(
  items: { name: string; qty: number; unit: string | null; grams: number; query: string; assumed: boolean }[],
  foods: CoreFood[],
): ResolvedItem[] {
  return items.map((item) => {
    const parsed: ParsedItem = {
      qty: item.qty, unit: item.unit, query: item.query, raw: item.name, assumedQty: item.assumed,
    };
    const matches = searchFoods(foods, item.query, 6);
    const food = matches[0]?.food ?? null;
    if (!food) {
      return { parsed, food: null, alternatives: [], grams: null, macros: null, issue: 'no-match' };
    }
    const alternatives = matches.slice(1).map((m) => m.food);
    const grams = gramsFor(food, item.qty, item.unit) ?? (item.grams > 0 ? item.grams : null);
    if (grams === null) {
      return { parsed, food, alternatives, grams: null, macros: null, issue: 'no-amount' };
    }
    return { parsed, food, alternatives, grams, macros: forGrams(macrosOf(food), grams), issue: null };
  });
}

/** Everything that resolved cleanly, ready to store. */
export function loggableItems(items: ResolvedItem[]): LoggedFood[] {
  return items.map(toLoggedFood).filter((x): x is LoggedFood => x !== null);
}

void bestMatch;
